
import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { updateRisk } from "../../api/riskApi";

const EditRiskModal = ({ isOpen, onClose, risk, onRiskUpdated }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Operational",
    likelihood: "Low",
    impact: "Low",
    riskOwner: "",
    mitigation: "",
    contingencyPlan: "",
    status: "Open",
    causes: "",
    consequences: "",
    rating: "Medium",
    comments: "",
    treatments: [],
  });

  const categories = ["Operational", "Compliance", "Financial", "Technical", "Other"];
  const statuses = ["Open", "In Progress", "Resolved", "Closed"];
  const ratings = ["Low", "Medium", "High"];

  // ✅ Pre-fill form with risk data
  useEffect(() => {
    if (risk) {
      setFormData({
        title: risk.title || "",
        description: risk.description || "",
        category: risk.category || "Operational",
        likelihood: risk.likelihood || "Low",
        impact: risk.impact || "Low",
        riskOwner: risk.riskOwner || "",
        mitigation: risk.mitigation || "",
        contingencyPlan: risk.contingencyPlan || "",
        status: risk.status || "Open",
        causes: risk.causes || "",
        consequences: risk.consequences || "",
        rating: risk.rating || "Medium",
        comments: risk.comments || "",
        treatments: risk.treatments?.length ? risk.treatments : [],
      });
    }
  }, [risk]);

  if (!isOpen) return null;

  // ✅ Generic change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);
  };

  // ✅ Treatment change handler (immutable)
  const handleTreatmentChange = (index, field, value) => {
    const updatedTreatments = formData.treatments.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    );
    setFormData({ ...formData, treatments: updatedTreatments });
  };

  const addTreatment = () => {
    setFormData({
      ...formData,
      treatments: [...formData.treatments, { action: "", responsible: "", dueDate: "" }],
    });
  };

  const removeTreatment = (index) => {
    setFormData({
      ...formData,
      treatments: formData.treatments.filter((_, i) => i !== index),
    });
  };

  // ✅ Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedRisk = await updateRisk(risk._id, formData);
      if (onRiskUpdated) onRiskUpdated(updatedRisk); // only trigger update after save
      onClose(); // close only here
    } catch (err) {
      console.error(err);
      alert("Failed to update risk.");
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50 p-2">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Edit Risk</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-red-500">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          {/* Treatments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Treatment / Actions</label>
            {formData.treatments.map((t, idx) => (
              <div key={idx} className="flex flex-col sm:flex-row gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Action"
                  value={t.action}
                  onChange={(e) => handleTreatmentChange(idx, "action", e.target.value)}
                  className="border rounded px-2 py-1 flex-1"
                />
                <input
                  type="text"
                  placeholder="Responsible"
                  value={t.responsible}
                  onChange={(e) => handleTreatmentChange(idx, "responsible", e.target.value)}
                  className="border rounded px-2 py-1 flex-1"
                />
                <input
                  type="date"
                  value={t.dueDate}
                  onChange={(e) => handleTreatmentChange(idx, "dueDate", e.target.value)}
                  className="border rounded px-2 py-1 w-36"
                />
                <button
                  type="button"
                  onClick={() => removeTreatment(idx)}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addTreatment}
              className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
            >
              Add Treatment
            </button>
          </div>

          {/* Likelihood + Impact */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Likelihood</label>
              <select
                name="likelihood"
                value={formData.likelihood}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
              <select
                name="impact"
                value={formData.impact}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
              </select>
            </div>
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
            <select
              name="rating"
              value={formData.rating}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 text-sm"
            >
              {ratings.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 text-sm"
            >
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Risk Owner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Risk Owner</label>
            <input
              type="text"
              name="riskOwner"
              value={formData.riskOwner}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          {/* Mitigation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mitigation</label>
            <textarea
              name="mitigation"
              value={formData.mitigation}
              onChange={handleChange}
              rows={2}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          {/* Contingency Plan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contingency Plan</label>
            <textarea
              name="contingencyPlan"
              value={formData.contingencyPlan}
              onChange={handleChange}
              rows={2}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          {/* Causes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Causes</label>
            <textarea
              name="causes"
              value={formData.causes}
              onChange={handleChange}
              rows={2}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          {/* Consequences */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Consequences</label>
            <textarea
              name="consequences"
              value={formData.consequences}
              onChange={handleChange}
              rows={2}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          {/* Comments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              rows={2}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 text-sm"
            >
              {statuses.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
            >
              Save & Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRiskModal;


